import React, { useEffect, useRef, useState } from 'react'
import * as Styled from '../../containers/addAssessmentQuestion/styled';
import { functionService } from '../../lib/functions';
import uploadIcon from '../../images/upload.svg';

const FOLDER_ORDER = (folder) => {
    if (folder === "grader") return 0;
    if (folder === "solution") return 1;
    if (folder === "sample") return 2;
    const n = Number(String(folder).replace("hidden", ""));
    return Number.isFinite(n) ? 2 + n : 1000;
};

const formatSize = (bytes) => (bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} KB`);

const fileRole = (folder, name) => {
    if (folder === "grader") return name === "grader.sh" ? "grader" : "support file";
    if (folder === "solution") return "reference solution";
    if (name === "expected.txt") return "expected output";
    if (name === "explanation.txt") return "sample explanation";
    return "input file";
};

const treeStyles = {
    box: { border: '1px solid #E5E7EB', borderRadius: '10px', background: '#FFFFFF', padding: '14px 16px', fontFamily: 'Menlo, Monaco, Consolas, monospace', fontSize: '13px', lineHeight: 1.8 },
    folder: { fontWeight: 600, color: '#1E2228' },
    row: { display: 'flex', alignItems: 'center', gap: '10px', paddingLeft: '22px' },
    name: { color: '#1E2228', minWidth: '160px' },
    size: { color: '#9CA3AF', minWidth: '70px' },
    tag: { fontFamily: 'inherit', fontSize: '11px', padding: '0 8px', borderRadius: '999px', background: '#EDF0FE', color: '#1B49EA' },
    skipped: { fontFamily: 'inherit', fontSize: '11px', padding: '0 8px', borderRadius: '999px', background: '#F3F4F6', color: '#6B7280' },
};

const treeFromSaved = (data) => {
    const baseName = (p) => String(p || "").split("/").pop();
    const folders = {
        grader: [
            { name: "grader.sh", path: data.grader_path_s3 },
            ...(data.support_files || []).map((s) => ({ name: baseName(s.path), path: s.path })),
        ],
    };
    if (data.solution_path_s3) folders.solution = [{ name: "solve.sh", path: data.solution_path_s3 }];
    (data.test_cases || []).forEach((tc) => {
        folders[tc.folder] = [
            ...tc.input_files.map((f) => ({ name: f.name, path: f.path_s3 })),
            { name: "expected.txt", path: tc.result_file_s3 },
        ];
    });
    return Object.keys(folders)
        .sort((a, b) => FOLDER_ORDER(a) - FOLDER_ORDER(b))
        .map((folder) => ({
            folder,
            files: folders[folder].map((f) => ({ ...f, size: null })).sort((a, b) => a.name.localeCompare(b.name)),
        }));
};

function BashComponent({onChange, initialData}){

    const folderInputRef = useRef(null);
    const [folderUploading, setFolderUploading] = useState(false);
    const [folderError, setFolderError] = useState("");
    const [tree, setTree] = useState([]);           // [{ folder, files: [{ name, size, path, skipped }] }]
    const [skippedFiles, setSkippedFiles] = useState([]);
    const [verifying, setVerifying] = useState(false);
    const [verifyResult, setVerifyResult] = useState(null);
    const [verifyError, setVerifyError] = useState("");
    const [isVerified,setIsVerified] = useState(false);
    const [bashDetails, setBashDetails] = useState({
        active: true,
        question_folder: "", 
        grader_path_s3: "",
        grader_path_container: "",
        run_command: "",
        solution_path_s3: "",
        support_files: [],        // [{ path, dest, mode }]
        test_cases: [],           // [{ folder, is_sample, input_files: [{ name, path_s3 }], result_file_s3 }]
        sample_explanation: "",
        hints: [],
    })

    const uploadQuestionFolder = async (e)=>{
        const files = Array.from(e.target.files || []).filter((f) => !f.name.startsWith("."));
        e.target.value = "";
        if (!files.length) return;
        setFolderError("");
        setVerifyResult(null);
        setIsVerified(false);
        setVerifyError("");
        setFolderUploading(true);
        try{
            const formData = new FormData();
            files.forEach((f) => formData.append("files", f, f.webkitRelativePath || f.name));

            const res = await functionService.postFormData("bash/upload-folder", formData);
            if (!res.status) {
                throw new Error(res.error?.response?.data?.meta?.message || "Upload failed");
            }
            const { question, files: uploaded, skipped = [] } = res.response.data.data;

            const localByPath = {};
            files.forEach((f) => { localByPath[f.webkitRelativePath || f.name] = f; });
            const readLocal = async (rel) => (localByPath[rel] ? (await localByPath[rel].text()).trim() : "");

            let config = {};
            const configText = await readLocal(`${question}/question.json`);
            if (configText) {
                try { config = JSON.parse(configText); }
                catch (err) { throw new Error("question.json is not valid JSON"); }
            }
            const explanation = await readLocal(`${question}/sample/explanation.txt`);

            const graderFiles = uploaded.filter((f) => f.folder === "grader");
            const grader = graderFiles.find((f) => f.name === "grader.sh");
            const solution = uploaded.find((f) => f.folder === "solution" && f.name === "solve.sh");
            const caseFolders = [...new Set(uploaded.map((f) => f.folder))]
                .filter((f) => f !== "grader" && f !== "solution")
                .sort((a, b) => FOLDER_ORDER(a) - FOLDER_ORDER(b));
            const testCases = caseFolders.map((folder) => {
                const caseFiles = uploaded.filter((f) => f.folder === folder);
                return {
                    folder,
                    is_sample: folder === "sample",
                    input_files: caseFiles
                        .filter((f) => f.name !== "expected.txt" && f.name !== "explanation.txt")
                        .map((f) => ({ name: f.name, path_s3: f.path })),
                    result_file_s3: caseFiles.find((f) => f.name === "expected.txt")?.path || "",
                };
            });

            setBashDetails((prev) => ({
                ...prev,
                question_folder: question,
                grader_path_s3: grader ? grader.path : "",
                grader_path_container: config.grader_path_container || `/grader-${question.replace(/^q-/, "")}/grader.sh`,
                run_command: config.run_command || "",
                solution_path_s3: solution ? solution.path : "",
                support_files: graderFiles
                    .filter((f) => f.name !== "grader.sh")
                    .map((f) => {
                        const custom = (config.support_files || []).find((s) => s.name === f.name) || {};
                        return { path: f.path, dest: custom.dest || `/opt/lab-mock/${f.name}`, mode: custom.mode || "755" };
                    }),
                test_cases: testCases,
                sample_explanation: explanation,
                hints: Array.isArray(config.hints) ? config.hints : [],
            }));

            const folders = {};
            uploaded.forEach((f) => {
                const rel = `${question}/${f.folder}/${f.name}`;
                (folders[f.folder] = folders[f.folder] || []).push({
                    name: f.name, path: f.path, size: localByPath[rel]?.size || 0,
                });
            });
            setTree(
                Object.keys(folders)
                    .sort((a, b) => FOLDER_ORDER(a) - FOLDER_ORDER(b))
                    .map((folder) => ({ folder, files: folders[folder].sort((a, b) => a.name.localeCompare(b.name)) }))
            );
            setSkippedFiles(skipped.filter((s) => s !== `${question}/question.json`));

            const warnings = [];
            if (!grader) warnings.push("grader/grader.sh not found");
            if (!solution) warnings.push("solution/solve.sh not found, so the grader check cannot run");
            if (!testCases.some((t) => t.is_sample)) warnings.push("no sample/ folder");
            testCases.filter((t) => !t.result_file_s3).forEach((t) => warnings.push(`${t.folder}/expected.txt missing`));
            if (warnings.length) setFolderError(warnings.join(" · "));
        }catch(err){
            console.log("Error in uploading the question folder", err);
            setTree([]);
            setFolderError(err.message || "Error in uploading the question folder");
        }
        finally{
            setFolderUploading(false);
        }
    }
    // edit page: show the saved question until the admin re-uploads a folder
    useEffect(()=>{
        if (!initialData) return;
        setBashDetails((prev) => ({ ...prev, ...initialData }));
        setTree(treeFromSaved(initialData));
        setSkippedFiles([]);
        setIsVerified(true);
    },[initialData]);

    useEffect(()=>{
        if (onChange){
            onChange({...bashDetails,grader_check_passed:isVerified===true})
        }
    },[bashDetails,isVerified]);

    const verifyQuestion = async ()=>{
        setVerifyError("");
        setVerifyResult(null);
        setIsVerified(false);
        setVerifying(true);
        try{
            const res = await functionService.post("bash/verify", { ...bashDetails });
            if (!res.status) {
                throw new Error(res.error?.response?.data?.meta?.message || "Grader check failed to run");
            }
            setVerifyResult(res.response.data.data);
            setIsVerified(res.response.data.data.passed===true);
        }catch(err){
            console.log("Error in verifying the bash question", err);
            setVerifyError(err.message || "Grader check failed to run");
        }
        finally{
            setVerifying(false);
            
        }
    }

    const sampleCount = bashDetails.test_cases.filter((t) => t.is_sample).length;
    const hiddenCount = bashDetails.test_cases.length - sampleCount;
    const canVerify = Boolean(bashDetails.grader_path_s3 && bashDetails.solution_path_s3 && bashDetails.test_cases.length);

    return(
        <Styled.GreyBoxDiv>
            <Styled.GreyBoxHeader>
            <Styled.GreyBoxTitle>Bash Question</Styled.GreyBoxTitle>
            <Styled.GreyBoxHint>Upload the question folder, then run the grader check once before saving</Styled.GreyBoxHint>
            </Styled.GreyBoxHeader>

            <Styled.SubSectionTitle>Question Folder</Styled.SubSectionTitle>
            <Styled.SubSectionHint>
            Stored in S3 under bash/&lt;folder&gt;/ with the same structure. question.json is optional and is read in the browser only.
            </Styled.SubSectionHint>
            {/* <Styled.BashInfoNote style={{ marginTop: 0, marginBottom: '12px' }}>
                <code style={{ whiteSpace: 'pre', lineHeight: 1.6 }}>
{`q-disk-alert/
├── question.json  run_command, grader_path_container, hints, support_files (optional)
├── grader/        grader.sh + support files (e.g. mock df)
├── solution/      solve.sh (reference solution, used only by the grader check)
├── sample/        input files, expected.txt, explanation.txt
└── hidden1/ ...   input files, expected.txt`}
                </code>
            </Styled.BashInfoNote> */}
            <Styled.SqlInputWrap>
                <Styled.BashPathField>
                <input
                    type="text"
                    placeholder="No folder uploaded"
                    value={bashDetails.question_folder ? `bash/${bashDetails.question_folder}/` : ""}
                    readOnly
                    style={{ background: '#F9FAFB', color: '#4B5563' }}
                />
                <input
                    type="file"
                    ref={folderInputRef}
                    webkitdirectory=""
                    directory=""
                    multiple
                    onChange={uploadQuestionFolder}
                    style={{ display: 'none' }}
                />
                <Styled.BashUploadButton
                    type="button"
                    onClick={() => folderInputRef.current.click()}
                    disabled={folderUploading || verifying}>
                    <img src={uploadIcon} alt="" />
                    {folderUploading ? "Uploading..." : bashDetails.question_folder ? "Re-upload folder" : "Upload folder"}
                </Styled.BashUploadButton>
                </Styled.BashPathField>
                { folderError ? <Styled.InputErrorDiv>{folderError}</Styled.InputErrorDiv> : null }
            </Styled.SqlInputWrap>

            {tree.length ? (
                <>
                <Styled.SubSectionTitle style={{ marginTop: '24px' }}>Uploaded Files</Styled.SubSectionTitle>
                <Styled.SubSectionHint>
                {bashDetails.test_cases.length} test case(s) · {sampleCount} SAMPLE · {hiddenCount} HIDDEN
                {bashDetails.run_command ? " · run command from question.json" : " · default run command"}
                {bashDetails.hints.length ? ` · ${bashDetails.hints.length} hint(s)` : ""}
                </Styled.SubSectionHint>
                <div style={treeStyles.box}>
                    <div style={treeStyles.folder}>bash/{bashDetails.question_folder}/</div>
                    {tree.map(({ folder, files }) => (
                        <div key={folder} style={{ paddingLeft: '22px' }}>
                        <div style={treeStyles.folder}>
                            {folder}/
                            {folder === "sample" ? <span style={{ ...treeStyles.tag, marginLeft: '8px' }}>visible to candidate</span> : null}
                            {folder.startsWith("hidden") ? <span style={{ ...treeStyles.skipped, marginLeft: '8px' }}>hidden</span> : null}
                        </div>
                        {files.map((file) => {
                            const support = folder === "grader" && file.name !== "grader.sh"
                                ? bashDetails.support_files.find((s) => s.path === file.path)
                                : null;
                            return (
                                <div key={file.path} style={treeStyles.row} title={file.path}>
                                <span style={treeStyles.name}>{file.name}</span>
                                <span style={treeStyles.size}>{file.size === null ? "saved" : formatSize(file.size)}</span>
                                <span style={treeStyles.tag}>
                                    {fileRole(folder, file.name)}{support ? ` → ${support.dest} (${support.mode})` : ""}
                                </span>
                                </div>
                            );
                        })}
                        </div>
                    ))}
                    {skippedFiles.map((rel) => (
                        <div key={rel} style={treeStyles.row}>
                        <span style={{ ...treeStyles.name, color: '#9CA3AF' }}>{rel}</span>
                        <span style={treeStyles.skipped}>not uploaded</span>
                        </div>
                    ))}
                </div>

                <Styled.SubSectionTitle style={{ marginTop: '24px' }}>Grader Check</Styled.SubSectionTitle>
                <Styled.SubSectionHint>
                Runs solution/solve.sh on every test case in a lab container and grades it with grader/grader.sh.
                Every case should pass before you save the question.
                </Styled.SubSectionHint>
                <Styled.SqlButton type="button" onClick={verifyQuestion} disabled={!canVerify || verifying || folderUploading}>
                    {verifying ? "Running grader..." : "Run grader check"}
                </Styled.SqlButton>
                { verifyError ? <Styled.InputErrorDiv>{verifyError}</Styled.InputErrorDiv> : null }

                {verifyResult ? (
                    <div style={{ ...treeStyles.box, marginTop: '12px', fontFamily: 'inherit' }}>
                        <div style={{ fontWeight: 600, marginBottom: '8px', color: verifyResult.passed ? '#047857' : '#B91C1C' }}>
                        {verifyResult.passed
                            ? `All ${verifyResult.results.length} test case(s) passed`
                            : `${verifyResult.results.filter((r) => !r.passed).length} of ${verifyResult.results.length} test case(s) failed`}
                        </div>
                        {verifyResult.results.map((result) => (
                            <div key={result.folder} style={{ borderTop: '1px solid #F3F4F6', padding: '8px 0' }}>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <span style={{ fontWeight: 600, color: result.passed ? '#047857' : '#B91C1C', minWidth: '48px' }}>
                                {result.passed ? "PASS" : "FAIL"}
                                </span>
                                <span style={{ fontFamily: 'Menlo, Monaco, Consolas, monospace', minWidth: '90px' }}>{result.folder}/</span>
                                <span style={{ color: '#4B5563' }}>
                                {(result.checks || []).map((c) => c.comment).join(" · ") || result.summary}
                                </span>
                            </div>
                            {!result.passed && result.output !== undefined ? (
                                <pre style={{ margin: '6px 0 0 58px', padding: '8px 10px', background: '#F9FAFB', borderRadius: '6px', fontSize: '12px', whiteSpace: 'pre-wrap' }}>
                                {result.output || "(no output)"}
                                </pre>
                            ) : null}
                            </div>
                        ))}
                    </div>
                ) : null}
                </>
            ) : null}
        </Styled.GreyBoxDiv>
    )
}

export default BashComponent;
